/**
 * Product Management Screen
 *
 * Allows managers to set whether individual products are available or out-of-stock.
 * Status changes are persisted to Firestore and reflected in the sales rep order form.
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import {
  Package,
  Search,
  CheckCircle,
  XCircle,
  ToggleLeft,
  ToggleRight,
  RefreshCw,
  Filter,
} from 'lucide-react';
import { ThemeColors, Spacing, Typography, BorderRadius, Shadows, Transitions } from '../../styles/ThemeStyles';
import {
  PageContainer,
  MainContent,
  ContentSection,
} from '../../styles/StyledComponents';
import { Product, getAllProductsWithStatus, updateProductStatus } from '../../services/ProductService';
import { Supply, getAllSuppliesWithStatus, updateSupplyStatus } from '../../services/SupplyService';

// ─── Styled Components ───────────────────────────────────────────────────────

const PageHeader = styled.div`
  margin-bottom: ${Spacing.xl};
`;

const PageTitle = styled.h1`
  font-family: 'Roboto', sans-serif;
  font-size: 2rem;
  font-weight: 500;
  color: ${ThemeColors.secondary.main};
  margin: 0 0 ${Spacing.xs} 0;
  display: flex;
  align-items: center;
  gap: ${Spacing.md};
`;

const PageSubtitle = styled.p`
  color: ${ThemeColors.text.secondary};
  font-size: ${Typography.fontSize.sm};
  margin: 0;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: ${Spacing.md};
  margin-bottom: ${Spacing.xl};

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const StatCard = styled.div<{ color?: string }>`
  background: white;
  border: 1px solid ${ThemeColors.border.light};
  border-radius: ${BorderRadius.lg};
  padding: ${Spacing.lg};
  display: flex;
  flex-direction: column;
  gap: ${Spacing.xs};
  box-shadow: ${Shadows.sm};
  border-left: 4px solid ${({ color }) => color || ThemeColors.primary.main};
`;

const StatValue = styled.div`
  font-size: 2rem;
  font-weight: ${Typography.fontWeight.bold};
  color: ${ThemeColors.text.primary};
  line-height: 1;
`;

const StatLabel = styled.div`
  font-size: ${Typography.fontSize.sm};
  color: ${ThemeColors.text.secondary};
`;

const ControlsRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacing.md};
  margin-bottom: ${Spacing.lg};
  flex-wrap: wrap;
`;

const SearchWrapper = styled.div`
  position: relative;
  flex: 1;
  min-width: 200px;

  svg {
    position: absolute;
    left: ${Spacing.md};
    top: 50%;
    transform: translateY(-50%);
    color: ${ThemeColors.text.secondary};
    pointer-events: none;
  }
`;

const SearchField = styled.input`
  width: 100%;
  padding: ${Spacing.sm} ${Spacing.md} ${Spacing.sm} 2.75rem;
  border: 1px solid ${ThemeColors.border.light};
  border-radius: ${BorderRadius.md};
  font-size: ${Typography.fontSize.sm};
  background: white;
  box-sizing: border-box;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${ThemeColors.primary.main};
    box-shadow: 0 0 0 2px ${ThemeColors.primary.light};
  }
`;

const FilterSelect = styled.select`
  padding: ${Spacing.sm} ${Spacing.md};
  border: 1px solid ${ThemeColors.border.light};
  border-radius: ${BorderRadius.md};
  font-size: ${Typography.fontSize.sm};
  background: white;
  cursor: pointer;
  min-width: 160px;

  &:focus {
    outline: none;
    border-color: ${ThemeColors.primary.main};
  }
`;

const BulkButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${Spacing.xs};
  padding: ${Spacing.sm} ${Spacing.md};
  background: white;
  border: 1px solid ${ThemeColors.border.light};
  border-radius: ${BorderRadius.md};
  font-size: ${Typography.fontSize.sm};
  font-weight: ${Typography.fontWeight.medium};
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.2s ease;
  color: ${ThemeColors.text.primary};

  &:hover {
    border-color: ${ThemeColors.primary.main};
    color: ${ThemeColors.primary.main};
    background: ${ThemeColors.primary.light};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ProductTable = styled.div`
  background: white;
  border: 1px solid ${ThemeColors.border.light};
  border-radius: ${BorderRadius.lg};
  overflow: hidden;
  box-shadow: ${Shadows.sm};
`;

const TableHeader = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr 120px;
  gap: ${Spacing.md};
  padding: ${Spacing.md} ${Spacing.lg};
  background: ${ThemeColors.background.paper};
  border-bottom: 2px solid ${ThemeColors.border.light};
  font-size: ${Typography.fontSize.xs};
  font-weight: ${Typography.fontWeight.bold};
  color: ${ThemeColors.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.5px;

  @media (max-width: 768px) {
    display: none;
  }
`;

const TableRow = styled.div<{ isOutOfStock: boolean }>`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr 120px;
  gap: ${Spacing.md};
  padding: ${Spacing.md} ${Spacing.lg};
  border-bottom: 1px solid ${ThemeColors.border.light};
  align-items: center;
  transition: background 0.15s ease;
  opacity: ${({ isOutOfStock }) => (isOutOfStock ? 0.6 : 1)};

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: ${ThemeColors.background.paper};
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: ${Spacing.xs};
  }
`;

const ProductName = styled.div`
  font-weight: ${Typography.fontWeight.medium};
  color: ${ThemeColors.text.primary};
  font-size: ${Typography.fontSize.sm};
`;

const ProductCode = styled.div`
  font-family: 'Courier New', monospace;
  font-size: ${Typography.fontSize.xs};
  color: ${ThemeColors.text.secondary};
  margin-top: 2px;
`;

const CellText = styled.div`
  font-size: ${Typography.fontSize.sm};
  color: ${ThemeColors.text.secondary};
`;

const StatusBadge = styled.span<{ status: 'available' | 'out-of-stock' }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: ${Typography.fontSize.xs};
  font-weight: ${Typography.fontWeight.semiBold};
  padding: 3px 10px;
  border-radius: 20px;
  background: ${({ status }) =>
    status === 'available' ? '#dcfce7' : '#fee2e2'};
  color: ${({ status }) =>
    status === 'available' ? '#16a34a' : '#dc2626'};
`;

const ToggleButton = styled.button<{ isOutOfStock: boolean; loading?: boolean }>`
  display: flex;
  align-items: center;
  gap: ${Spacing.xs};
  padding: ${Spacing.xs} ${Spacing.md};
  border: 1px solid
    ${({ isOutOfStock }) =>
      isOutOfStock ? ThemeColors.error.main : ThemeColors.success.main};
  background: ${({ isOutOfStock }) =>
    isOutOfStock ? '#fee2e2' : '#dcfce7'};
  color: ${({ isOutOfStock }) =>
    isOutOfStock ? ThemeColors.error.main : '#16a34a'};
  border-radius: ${BorderRadius.md};
  font-size: ${Typography.fontSize.xs};
  font-weight: ${Typography.fontWeight.semiBold};
  cursor: ${({ loading }) => (loading ? 'not-allowed' : 'pointer')};
  opacity: ${({ loading }) => (loading ? 0.6 : 1)};
  transition: all 0.2s ease;
  white-space: nowrap;

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: ${Shadows.sm};
  }
`;

const MobileCard = styled.div<{ isOutOfStock: boolean }>`
  display: none;
  padding: ${Spacing.md} ${Spacing.lg};
  border-bottom: 1px solid ${ThemeColors.border.light};
  opacity: ${({ isOutOfStock }) => (isOutOfStock ? 0.65 : 1)};

  &:last-child {
    border-bottom: none;
  }

  @media (max-width: 768px) {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: ${Spacing.md};
  }
`;

const MobileCardInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const MobileCardMeta = styled.div`
  display: flex;
  gap: ${Spacing.sm};
  margin-top: ${Spacing.xs};
  flex-wrap: wrap;
`;

const MetaChip = styled.span`
  font-size: ${Typography.fontSize.xs};
  color: ${ThemeColors.text.secondary};
  background: ${ThemeColors.background.paper};
  padding: 2px 8px;
  border-radius: 20px;
  border: 1px solid ${ThemeColors.border.light};
`;

const EmptyState = styled.div`
  padding: ${Spacing.xxl};
  text-align: center;
  color: ${ThemeColors.text.secondary};
`;

const LoadingState = styled.div`
  padding: ${Spacing.xxl};
  text-align: center;
  color: ${ThemeColors.text.secondary};
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${Spacing.md};
`;

const Spinner = styled.div`
  width: 32px;
  height: 32px;
  border: 3px solid ${ThemeColors.border.light};
  border-top-color: ${ThemeColors.primary.main};
  border-radius: 50%;
  animation: spin 0.7s linear infinite;

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const FORM_TYPES = ['All', 'Vegetable', 'Flower', 'Heritage', 'Livingston'] as const;

type ProductManagementTab = 'store-products' | 'supplies';

const TabButton = styled.button<{ active?: boolean }>`
  padding: ${Spacing.sm} ${Spacing.lg};
  border: 1px solid ${ThemeColors.border.main};
  border-radius: ${BorderRadius.md};
  font-size: ${Typography.fontSize.sm};
  font-weight: ${Typography.fontWeight.medium};
  cursor: pointer;
  background: ${({ active }) => (active ? ThemeColors.primary.main : 'white')};
  color: ${({ active }) => (active ? 'white' : ThemeColors.text.primary)};
  transition: all 0.2s ease;

  &:hover {
    border-color: ${ThemeColors.primary.main};
    ${({ active }) => !active && `color: ${ThemeColors.primary.main};`}
  }

  &:first-of-type {
    border-radius: ${BorderRadius.md} 0 0 ${BorderRadius.md};
  }

  &:last-of-type {
    border-radius: 0 ${BorderRadius.md} ${BorderRadius.md} 0;
  }
`;

const TabGroup = styled.div`
  display: flex;
  gap: 0;
  margin-bottom: ${Spacing.xl};
  border-radius: ${BorderRadius.md};
  overflow: hidden;
`;

// ─── Component ────────────────────────────────────────────────────────────────

export const ProductManagementScreen: React.FC = () => {
  const { t } = useTranslation();

  const [activeTab, setActiveTab] = useState<ProductManagementTab>('store-products');
  const [products, setProducts] = useState<Product[]>([]);
  const [supplies, setSupplies] = useState<Supply[]>([]);
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState<Set<string>>(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [filterForm, setFilterForm] = useState<string>('All');
  const [filterStatus, setFilterStatus] = useState<string>('All');
  const [filterCategory, setFilterCategory] = useState<string>('All');
  const [filterSubCategory, setFilterSubCategory] = useState<string>('All');

  // ── Fetch products ───────────────────────────────────────────────────────
  const fetchProducts = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getAllProductsWithStatus();
      setProducts(data);
    } catch (err) {
      console.error('Error loading products:', err);
      toast.error('Failed to load products. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  // ── Fetch supplies ───────────────────────────────────────────────────────
  const fetchSupplies = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getAllSuppliesWithStatus();
      setSupplies(data);
    } catch (err) {
      console.error('Error loading supplies:', err);
      toast.error('Failed to load supplies. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'store-products') {
      fetchProducts();
    } else {
      fetchSupplies();
    }
  }, [activeTab, fetchProducts, fetchSupplies]);

  // ── Derived stats ────────────────────────────────────────────────────────
  const stats = useMemo(() => {
    const items = activeTab === 'store-products' ? products : supplies;
    const total = items.length;
    const available = items.filter((p: Product | Supply) => (p.status || 'available') === 'available').length;
    const outOfStock = total - available;
    return { total, available, outOfStock };
  }, [activeTab, products, supplies]);

  // ── Dynamic filter options ────────────────────────────────────────────────
  const categoryOptions = useMemo(() => {
    if (activeTab === 'supplies') {
      const categories = Array.from(
        new Set(supplies.map((s: Supply) => s.categoryEN || s.category).filter(Boolean))
      ).sort();
      return ['All', ...categories];
    }
    const categories = Array.from(
      new Set(products.map(p => p.ItemCategoryEN).filter(Boolean))
    ).sort();
    return ['All', ...categories];
  }, [activeTab, products, supplies]);

  const subCategoryOptions = useMemo(() => {
    if (activeTab === 'supplies') return ['All'];
    const subCategories = Array.from(
      new Set(products.map(p => p.ItemSubCategoryEN).filter(Boolean))
    ).sort();
    return ['All', ...subCategories];
  }, [activeTab, products]);

  // ── Filtered products ────────────────────────────────────────────────────
  const filteredProducts = useMemo(() => {
    const term = searchTerm.toLowerCase();
    return products.filter(p => {
      const matchSearch =
        !term ||
        p.ItemNameEN.toLowerCase().includes(term) ||
        p.ItemNameFR.toLowerCase().includes(term) ||
        p.ItemCode.includes(term);
      const matchForm = filterForm === 'All' || p.ItemForm === filterForm;
      const status = p.status || 'available';
      const matchStatus =
        filterStatus === 'All' ||
        (filterStatus === 'Available' && status === 'available') ||
        (filterStatus === 'Out of Stock' && status === 'out-of-stock');
      const matchCategory =
        filterCategory === 'All' || p.ItemCategoryEN === filterCategory;
      const matchSubCategory =
        filterSubCategory === 'All' || p.ItemSubCategoryEN === filterSubCategory;
      return (
        matchSearch &&
        matchForm &&
        matchStatus &&
        matchCategory &&
        matchSubCategory
      );
    });
  }, [products, searchTerm, filterForm, filterStatus, filterCategory, filterSubCategory]);

  // ── Filtered supplies ────────────────────────────────────────────────────
  const filteredSupplies = useMemo(() => {
    const term = searchTerm.toLowerCase();
    return supplies.filter((s: Supply) => {
      const desc = (s.description || s.descriptionEN || '').toLowerCase();
      const descFR = (s.descriptionFR || '').toLowerCase();
      const code = (s.ItemCode || s.code || '').toLowerCase();
      const cat = (s.category || s.categoryEN || '').toLowerCase();
      const matchSearch =
        !term || desc.includes(term) || descFR.includes(term) || code.includes(term) || cat.includes(term);
      const status = s.status || 'available';
      const matchStatus =
        filterStatus === 'All' ||
        (filterStatus === 'Available' && status === 'available') ||
        (filterStatus === 'Out of Stock' && status === 'out-of-stock');
      const matchCategory =
        filterCategory === 'All' || (s.categoryEN || s.category) === filterCategory;
      return matchSearch && matchStatus && matchCategory;
    });
  }, [supplies, searchTerm, filterStatus, filterCategory]);

  // ── Toggle handler (products) ─────────────────────────────────────────────
  const handleToggleProduct = useCallback(
    async (product: Product) => {
      const currentStatus = product.status || 'available';
      const newStatus: 'available' | 'out-of-stock' =
        currentStatus === 'available' ? 'out-of-stock' : 'available';
      const itemCode = product.ItemCode;

      setToggling(prev => new Set(prev).add(itemCode));

      setProducts(prev =>
        prev.map(p => (p.ItemCode === itemCode ? { ...p, status: newStatus } : p))
      );

      try {
        await updateProductStatus(itemCode, newStatus);
        toast.success(
          `"${product.ItemNameEN}" marked as ${newStatus === 'available' ? 'Available' : 'Out of Stock'}`
        );
      } catch (err) {
        setProducts(prev =>
          prev.map(p => (p.ItemCode === itemCode ? { ...p, status: currentStatus } : p))
        );
        toast.error('Failed to update product status. Please try again.');
      } finally {
        setToggling(prev => {
          const updated = new Set(prev);
          updated.delete(itemCode);
          return updated;
        });
      }
    },
    []
  );

  // ── Toggle handler (supplies) ─────────────────────────────────────────────
  const handleToggleSupply = useCallback(
    async (supply: Supply) => {
      const supplyId = supply.id!;
      const currentStatus = supply.status || 'available';
      const newStatus: 'available' | 'out-of-stock' =
        currentStatus === 'available' ? 'out-of-stock' : 'available';

      setToggling(prev => new Set(prev).add(supplyId));

      setSupplies(prev =>
        prev.map(s => (s.id === supplyId ? { ...s, status: newStatus } : s))
      );

      try {
        await updateSupplyStatus(supplyId, newStatus);
        const name = supply.description || supply.descriptionEN || supply.ItemCode || supply.code;
        toast.success(
          `"${name}" marked as ${newStatus === 'available' ? 'Available' : 'Out of Stock'}`
        );
      } catch (err) {
        setSupplies(prev =>
          prev.map(s => (s.id === supplyId ? { ...s, status: currentStatus } : s))
        );
        toast.error('Failed to update supply status. Please try again.');
      } finally {
        setToggling(prev => {
          const updated = new Set(prev);
          updated.delete(supplyId);
          return updated;
        });
      }
    },
    []
  );

  // ── Bulk: mark all available ───────────────────────────────────────────────
  const handleMarkAllAvailable = useCallback(async () => {
    if (activeTab === 'store-products') {
      const outOfStockProducts = filteredProducts.filter(
        p => (p.status || 'available') === 'out-of-stock'
      );
      if (outOfStockProducts.length === 0) return;

      const allCodes = new Set(outOfStockProducts.map(p => p.ItemCode));
      setToggling(prev => new Set([...prev, ...allCodes]));

      setProducts(prev =>
        prev.map(p => (allCodes.has(p.ItemCode) ? { ...p, status: 'available' } : p))
      );

      try {
        await Promise.all(outOfStockProducts.map(p => updateProductStatus(p.ItemCode, 'available')));
        toast.success(`${outOfStockProducts.length} product(s) marked as Available`);
      } catch (err) {
        toast.error('Some updates failed. Please refresh and try again.');
        fetchProducts();
      } finally {
        setToggling(new Set());
      }
    } else {
      const outOfStockSupplies = filteredSupplies.filter(
        (s: Supply) => (s.status || 'available') === 'out-of-stock'
      );
      if (outOfStockSupplies.length === 0) return;

      const allIds = new Set(outOfStockSupplies.map((s: Supply) => s.id!));
      setToggling(prev => new Set([...prev, ...allIds]));

      setSupplies(prev =>
        prev.map(s => (allIds.has(s.id!) ? { ...s, status: 'available' as const } : s))
      );

      try {
        await Promise.all(outOfStockSupplies.map((s: Supply) => updateSupplyStatus(s.id!, 'available')));
        toast.success(`${outOfStockSupplies.length} supply(ies) marked as Available`);
      } catch (err) {
        toast.error('Some updates failed. Please refresh and try again.');
        fetchSupplies();
      } finally {
        setToggling(new Set());
      }
    }
  }, [activeTab, filteredProducts, filteredSupplies, fetchProducts, fetchSupplies]);

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <PageContainer isMenuCollapsed={false}>
      <MainContent>
        <ContentSection>
          {/* Header */}
          <PageHeader>
            <PageTitle>
              <Package size={28} color={ThemeColors.primary.main} />
              Product Management
            </PageTitle>
            <PageSubtitle>
              Toggle availability to control what sales representatives can order.
              Out-of-stock items are greyed out in the order form.
            </PageSubtitle>
          </PageHeader>

          {/* Tab: Store Products vs Supplies */}
          <TabGroup>
            <TabButton
              active={activeTab === 'store-products'}
              onClick={() => setActiveTab('store-products')}
            >
              Store Order Products
            </TabButton>
            <TabButton
              active={activeTab === 'supplies'}
              onClick={() => setActiveTab('supplies')}
            >
              Supply Order Items
            </TabButton>
          </TabGroup>

          {/* Stats */}
          <StatsGrid>
            <StatCard color={ThemeColors.primary.main}>
              <StatValue>{loading ? '—' : stats.total}</StatValue>
              <StatLabel>Total {activeTab === 'supplies' ? 'Supplies' : 'Products'}</StatLabel>
            </StatCard>
            <StatCard color="#16a34a">
              <StatValue>{loading ? '—' : stats.available}</StatValue>
              <StatLabel>Available</StatLabel>
            </StatCard>
            <StatCard color={ThemeColors.error.main}>
              <StatValue>{loading ? '—' : stats.outOfStock}</StatValue>
              <StatLabel>Out of Stock</StatLabel>
            </StatCard>
          </StatsGrid>

          {/* Controls */}
          <ControlsRow>
            <SearchWrapper>
              <Search size={16} />
              <SearchField
                type="text"
                placeholder="Search by name, code..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
              />
            </SearchWrapper>

            {activeTab === 'store-products' && (
              <FilterSelect value={filterForm} onChange={e => setFilterForm(e.target.value)}>
                {FORM_TYPES.map(f => (
                  <option key={f} value={f}>
                    {f === 'All' ? 'All Categories' : f}
                  </option>
                ))}
              </FilterSelect>
            )}

            <FilterSelect value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
              <option value="All">All Statuses</option>
              <option value="Available">Available</option>
              <option value="Out of Stock">Out of Stock</option>
            </FilterSelect>

            <FilterSelect value={filterCategory} onChange={e => setFilterCategory(e.target.value)}>
              {categoryOptions.map(category => (
                <option key={category} value={category}>
                  {category === 'All' ? (activeTab === 'supplies' ? 'All Categories' : 'All Item Categories') : category}
                </option>
              ))}
            </FilterSelect>

            {activeTab === 'store-products' && (
              <FilterSelect value={filterSubCategory} onChange={e => setFilterSubCategory(e.target.value)}>
                {subCategoryOptions.map(subCategory => (
                  <option key={subCategory} value={subCategory}>
                    {subCategory === 'All' ? 'All Subcategories' : subCategory}
                  </option>
                ))}
              </FilterSelect>
            )}

            <BulkButton
              onClick={activeTab === 'store-products' ? fetchProducts : fetchSupplies}
              disabled={loading}
              title={activeTab === 'supplies' ? 'Refresh supplies' : 'Refresh products'}
            >
              <RefreshCw size={14} />
              Refresh
            </BulkButton>

            <BulkButton
              onClick={handleMarkAllAvailable}
              disabled={loading || stats.outOfStock === 0}
            >
              <CheckCircle size={14} />
              Mark All Available
            </BulkButton>
          </ControlsRow>

          {/* Product / Supply Table */}
          <ProductTable>
            {activeTab === 'store-products' ? (
              <>
                <TableHeader>
                  <div>Product</div>
                  <div>Code</div>
                  <div>Category</div>
                  <div>Price</div>
                  <div>Status</div>
                </TableHeader>
                {loading ? (
                  <LoadingState>
                    <Spinner />
                    Loading products…
                  </LoadingState>
                ) : filteredProducts.length === 0 ? (
                  <EmptyState>No products found matching your filters.</EmptyState>
                ) : (
                  filteredProducts.map(product => {
                    const isOutOfStock = (product.status || 'available') === 'out-of-stock';
                    const isLoading = toggling.has(product.ItemCode);
                    return (
                      <React.Fragment key={product.ItemCode}>
                        <TableRow isOutOfStock={isOutOfStock}>
                          <div>
                            <ProductName>{product.ItemNameEN}</ProductName>
                            <ProductCode>{product.ItemNameFR}</ProductCode>
                          </div>
                          <CellText style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                            {product.ItemCode}
                          </CellText>
                          <CellText>{product.ItemForm}</CellText>
                          <CellText>${product.Price.toFixed(2)}</CellText>
                          <div>
                            <ToggleButton
                              isOutOfStock={isOutOfStock}
                              loading={isLoading}
                              onClick={() => !isLoading && handleToggleProduct(product)}
                              disabled={isLoading}
                              title={isOutOfStock ? 'Mark as Available' : 'Mark as Out of Stock'}
                            >
                              {isLoading ? (
                                <RefreshCw size={12} style={{ animation: 'spin 0.7s linear infinite' }} />
                              ) : isOutOfStock ? (
                                <ToggleLeft size={14} />
                              ) : (
                                <ToggleRight size={14} />
                              )}
                              {isOutOfStock ? 'Out of Stock' : 'Available'}
                            </ToggleButton>
                          </div>
                        </TableRow>
                        <MobileCard isOutOfStock={isOutOfStock}>
                          <MobileCardInfo>
                            <ProductName>{product.ItemNameEN}</ProductName>
                            <MobileCardMeta>
                              <MetaChip>{product.ItemCode}</MetaChip>
                              <MetaChip>{product.ItemForm}</MetaChip>
                              <MetaChip>${product.Price.toFixed(2)}</MetaChip>
                            </MobileCardMeta>
                          </MobileCardInfo>
                          <ToggleButton
                            isOutOfStock={isOutOfStock}
                            loading={isLoading}
                            onClick={() => !isLoading && handleToggleProduct(product)}
                            disabled={isLoading}
                          >
                            {isLoading ? (
                              <RefreshCw size={12} />
                            ) : isOutOfStock ? (
                              <XCircle size={12} />
                            ) : (
                              <CheckCircle size={12} />
                            )}
                            {isOutOfStock ? 'Out of Stock' : 'Available'}
                          </ToggleButton>
                        </MobileCard>
                      </React.Fragment>
                    );
                  })
                )}
              </>
            ) : (
              <>
                <TableHeader>
                  <div>Supply Item</div>
                  <div>Code</div>
                  <div>Category</div>
                  <div>Retail</div>
                  <div>Status</div>
                </TableHeader>
                {loading ? (
                  <LoadingState>
                    <Spinner />
                    Loading supplies…
                  </LoadingState>
                ) : filteredSupplies.length === 0 ? (
                  <EmptyState>
                    {supplies.length === 0
                      ? 'No supplies found. Upload supplies to the Supplies collection in Firestore.'
                      : 'No supplies found matching your filters.'}
                  </EmptyState>
                ) : (
                  filteredSupplies.map((supply: Supply) => {
                    const isOutOfStock = (supply.status || 'available') === 'out-of-stock';
                    const isLoading = toggling.has(supply.id!);
                    const desc = supply.description || supply.descriptionEN || '';
                    return (
                      <React.Fragment key={supply.id}>
                        <TableRow isOutOfStock={isOutOfStock}>
                          <div>
                            <ProductName>{desc.split('\n')[0] || desc}</ProductName>
                            <ProductCode>{supply.descriptionFR || ''}</ProductCode>
                          </div>
                          <CellText style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                            {supply.ItemCode || supply.code}
                          </CellText>
                          <CellText>{supply.categoryEN || supply.category}</CellText>
                          <CellText>{supply.retail || '—'}</CellText>
                          <div>
                            <ToggleButton
                              isOutOfStock={isOutOfStock}
                              loading={isLoading}
                              onClick={() => !isLoading && handleToggleSupply(supply)}
                              disabled={isLoading}
                              title={isOutOfStock ? 'Mark as Available' : 'Mark as Out of Stock'}
                            >
                              {isLoading ? (
                                <RefreshCw size={12} style={{ animation: 'spin 0.7s linear infinite' }} />
                              ) : isOutOfStock ? (
                                <ToggleLeft size={14} />
                              ) : (
                                <ToggleRight size={14} />
                              )}
                              {isOutOfStock ? 'Out of Stock' : 'Available'}
                            </ToggleButton>
                          </div>
                        </TableRow>
                        <MobileCard isOutOfStock={isOutOfStock}>
                          <MobileCardInfo>
                            <ProductName>{desc.split('\n')[0] || desc}</ProductName>
                            <MobileCardMeta>
                              <MetaChip>{supply.ItemCode || supply.code}</MetaChip>
                              <MetaChip>{supply.categoryEN || supply.category}</MetaChip>
                              <MetaChip>{supply.retail || '—'}</MetaChip>
                            </MobileCardMeta>
                          </MobileCardInfo>
                          <ToggleButton
                            isOutOfStock={isOutOfStock}
                            loading={isLoading}
                            onClick={() => !isLoading && handleToggleSupply(supply)}
                            disabled={isLoading}
                          >
                            {isLoading ? (
                              <RefreshCw size={12} />
                            ) : isOutOfStock ? (
                              <XCircle size={12} />
                            ) : (
                              <CheckCircle size={12} />
                            )}
                            {isOutOfStock ? 'Out of Stock' : 'Available'}
                          </ToggleButton>
                        </MobileCard>
                      </React.Fragment>
                    );
                  })
                )}
              </>
            )}
          </ProductTable>
        </ContentSection>
      </MainContent>
    </PageContainer>
  );
};
